/**
 * Chat room id vocabulary.
 *
 * Two shapes are valid:
 *
 *   1. Reserved labels — one of `RESERVED_CHAT_ROOM_IDS`. These map to
 *      env-configured UGM addresses (see `getUgmConfig` in
 *      `app/api/chat/seat-verify.ts`) and keep historical gates / messages
 *      stable across env changes.
 *
 *   2. Live composite grid ids — `0x<40 hex>-<tokenId>`. Every community /
 *      curated board in the lobby uses this shape (same format the live
 *      grid feed emits), so every live board automatically gets its own
 *      chat room without per-board env plumbing.
 *
 * The DB column `chat_messages.room` is plain TEXT with no CHECK constraint,
 * so any validated string works — the type guards here are the single source
 * of truth for what the server and UI accept.
 */
export const RESERVED_CHAT_ROOM_IDS = ['boardroom', 'megapot-grid'] as const;

/** @deprecated Prefer `RESERVED_CHAT_ROOM_IDS` — kept for external callers. */
export const CHAT_ROOM_IDS = RESERVED_CHAT_ROOM_IDS;

export type ReservedChatRoomId = (typeof RESERVED_CHAT_ROOM_IDS)[number];

/** A chat room id is either a reserved label or a composite grid id. */
export type ChatRoomId = string;

/** `0xUGM-tokenId` — mirrors `isLiveGridId` in `features/lobby/data/boardroom-grid.ts`. */
export const LIVE_CHAT_ROOM_PATTERN = /^0x[a-fA-F0-9]{40}-\d+$/;

export function isReservedChatRoomId(room: string): room is ReservedChatRoomId {
  return (RESERVED_CHAT_ROOM_IDS as readonly string[]).includes(room);
}

export function isLiveChatRoomId(room: string): boolean {
  return LIVE_CHAT_ROOM_PATTERN.test(room);
}

export function isChatRoomId(room: string): room is ChatRoomId {
  return isReservedChatRoomId(room) || isLiveChatRoomId(room);
}

/**
 * Human-readable labels for reserved chat rooms. Composite grid ids don't
 * appear here — callers that need a friendly label for a live board
 * should resolve it from board metadata (name / title) and fall through
 * to the raw id only as a last resort.
 */
export const CHAT_ROOM_DISPLAY_NAMES: Record<ReservedChatRoomId, string> = {
  'boardroom': 'The Boardroom',
  'megapot-grid': 'Megapot',
};

/**
 * Resolve a chat room id into a display name. Reserved labels use their
 * canonical name; composite grid ids fall through unchanged (callers
 * should substitute a board name where one is known). Unknown strings
 * pass through too so notifications never render blank.
 */
export function getChatRoomDisplayName(room: string | null | undefined): string {
  if (room && isReservedChatRoomId(room)) return CHAT_ROOM_DISPLAY_NAMES[room];
  return room ?? 'Chat';
}
